import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import ImgsViewer from 'react-images-viewer';
import { Link, Loading } from '@carbon/react';
import { definePage, defineResource, ListQuery } from '@restavo/crud';
import EmptyState from 'src/components/Base/EmptyState';
import BranchForm from 'src/components/Branch/BranchForm';
import { filterInMemory } from 'src/crud/filterInMemory';
import { useCurrentRestaurant } from 'src/hooks/useRestaurants';
import {
  orgRestaurantBranchCreate,
  orgRestaurantBranchDelete,
  orgRestaurantBranches,
  orgRestaurantBranchUpdate,
} from 'src/services/apis/tenantRequests';
import { RestaurantScope } from 'src/services/apis/tenantRequests';
import { RestaurantBranchCreateDto, RestaurantBranchEntity, RestaurantBranchUpdateDto } from 'src/services/apis/types';
import { getImageUrl } from 'src/utils/images';
import { QUERIES } from 'src/utils/react-query';

const restaurantBranches = defineResource({
  key: QUERIES.BRANCHES,
  // The generated branches wrapper takes no query params, so — exactly like the
  // hand-written hook — the server (alwaysPaginate, no configured take) returns every row
  // in one shot; the crud layer's paginated query still needs a concrete page size, and
  // 1000 is the practical match for that unbounded reach.
  pageSize: 1000,
  api: {
    // The org-scoped branch functions take (body, scope) / (id, body, scope) — the
    // adapters line them up with the crud layer's (scope, ...) calling order.
    // orgRestaurantBranches sends no query, so the ordering chips apply over the whole list
    // (a restaurant's branches all arrive in one response).
    list: async (scope: RestaurantScope, query?: ListQuery) => {
      const result = await orgRestaurantBranches(scope);
      const rows = filterInMemory(result.data, query?.filter);
      return { ...result, data: rows, total: rows.length };
    },
    create: (scope: RestaurantScope, body: RestaurantBranchCreateDto) => orgRestaurantBranchCreate(body, scope),
    update: (scope: RestaurantScope, id: string, body: RestaurantBranchUpdateDto) =>
      orgRestaurantBranchUpdate(id, body, scope),
    remove: (scope: RestaurantScope, id: string) => orgRestaurantBranchDelete(id, scope),
  },
  // The hand-written page filtered name/slug in memory over the fetched set.
  search: 'client',
  searchFields: ['name', 'slug'],
  filterable: [
    { field: 'publicOrderingEnabled', labelKey: 'common.publicOrderingEnabled', type: 'boolean' },
    { field: 'zaloOrderingEnabled', labelKey: 'common.zaloOrderingEnabled', type: 'boolean' },
  ],
});

/**
 * The RestaurantBranches page config, as a hook because the photo cell opens the image
 * viewer — page-specific runtime data, exactly like the pilot's movements modal.
 */
export const useRestaurantBranchesPage = () => {
  const { t } = useTranslation();
  const currentRestaurant = useCurrentRestaurant();
  const [viewingBranchPhotos, setViewingBranchPhotos] = useState<RestaurantBranchEntity[]>([]);

  const page = definePage(restaurantBranches, {
    titleKey: 'titles.branchSettingsPage',
    descriptionKey: 'common.branches',
    createLabelKey: 'actions.createNewBranch',
    updateLabelKey: 'actions.updateBranch',
    deleteHeadingKey: 'messages.areYouSureToDeleteThisBranch',
    columns: [
      // The column a user edits a branch by must survive every prefs reconciliation.
      // Not sortable: the branches list wrapper passes no query params, so a sort could
      // never reach the server — and the hand-written page had no sortable headers either.
      { key: 'name', headerKey: 'common.name', alwaysVisible: true },
      { key: 'slug', headerKey: 'common.slug' },
      { key: 'address', headerKey: 'common.address' },
      {
        key: 'photo',
        headerKey: 'common.photo',
        render: (row: RestaurantBranchEntity) =>
          row.photo && <Link onClick={() => setViewingBranchPhotos([row])}>{row.photo}</Link>,
      },
    ],
    form: BranchForm,
    emptyState: <EmptyState />,
    toPayload: (values: any, params) => ({
      ...values,
      restaurantId: params.restaurantId,
      latitude: parseFloat(values.latitude),
      longitude: parseFloat(values.longitude),
      salesGoalPerDay: Number(values.salesGoalPerDay || 0),
      salesGoalPerWeek: Number(values.salesGoalPerWeek || 0),
      salesGoalPerMonth: Number(values.salesGoalPerMonth || 0),
      salesGoalPerYear: Number(values.salesGoalPerYear || 0),
    }),
    useTitleVars: () => ({ restaurantName: currentRestaurant?.name }),
  });

  const photoViewer = (
    <ImgsViewer
      imgs={viewingBranchPhotos.map((item) => ({
        src: getImageUrl(item.photo),
        caption: `${currentRestaurant?.name} - ${item.name} ${t('common.photos')}`,
      }))}
      showCloseBtn={false}
      backdropCloseable={true}
      className={'cds--images-viewer'}
      width={'calc(100vw - 16rem)'}
      isOpen={viewingBranchPhotos.length > 0}
      onClickPrev={() => null}
      onClickNext={() => null}
      spinnerSize={10}
      spinner={() => <Loading className={'some-class'} withOverlay={false} />}
      onClose={() => setViewingBranchPhotos([])}
    />
  );

  return { page, photoViewer };
};
